// dashboard/views/ClinicSettingsView.js
// Extracted from App.js (2026-08-12 split).

import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import {
  buildPatientMonitoringGuideDoc,
  downloadPatientMonitoringGuide,
  imageUrlToPngDataUrl,
  svgUrlToPngDataUrl,
  formatPhoneForDisplay,
} from '../../patientMonitoringGuide';
import { supabase } from '../../supabase';
import { c } from '../constants';
import cairnHorizontalLogo from '../../assets/cairn-horizontal.svg';
import cairnMarkSlate from '../../assets/cairn-mark-slate.svg';
import dogCatOutline from '../../assets/dog-cat-outline.svg';
import appStoreBadge from '../../assets/store-badges/download-on-the-app-store.svg';
import googlePlayBadge from '../../assets/store-badges/get-it-on-google-play.png';

const APP_STORE_URL    = process.env.REACT_APP_APP_STORE_URL    || '';
const GOOGLE_PLAY_URL  = process.env.REACT_APP_GOOGLE_PLAY_URL  || '';

export default function ClinicSettingsView({ clinic, canManage, onClinicUpdate }) {
  const { t } = useTranslation();
  const [copied,     setCopied]     = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [guideError, setGuideError] = useState('');

  const [editingHospitals, setEditingHospitals] = useState(false);
  const [savingHospitals,  setSavingHospitals]  = useState(false);
  const [hospitalError,    setHospitalError]    = useState('');
  const [hospitalForm,     setHospitalForm]     = useState(null);

  function copyEnrollmentLink() {
    const link = `https://cairnpethealth.com/enroll/?id=${clinic?.id}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function copyCode() {
    if (!clinic?.enrollment_code) return;
    navigator.clipboard.writeText(clinic.enrollment_code).then(() => {
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    });
  }

  function downloadQR() {
    const canvas = document.getElementById('clinic-qr-canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `cairn-qr-${(clinic?.name || 'clinic').toLowerCase().replace(/\s+/g, '-')}.png`;
    a.click();
  }

  async function handleDownloadGuide() {
    if (generating) return; // prevent duplicate clicks while a guide is already generating
    setGuideError('');

    if (!clinic?.id) { setGuideError(t('clinicSettingsView.clinicLoadingError')); return; }
    if (!clinic?.enrollment_code && !clinic?.id) { setGuideError(t('clinicSettingsView.clinicMissingEnrollment')); return; }

    setGenerating(true);
    try {
      const enrollmentCanvas = document.getElementById('clinic-qr-canvas');
      if (!enrollmentCanvas) throw new Error(t('clinicSettingsView.qrNotReady'));
      const enrollmentQrDataUrl = enrollmentCanvas.toDataURL('image/png');

      // These are the approved Brand Pack v2.3 files copied into the dashboard
      // bundle verbatim. Artwork failure should not strand a clinic, but the
      // ordinary path always renders the official wordmark and mark.
      const [
        logoDataUrl,
        markDataUrl,
        petOutlineDataUrl,
        appStoreBadgeDataUrl,
        googlePlayBadgeDataUrl,
      ] = await Promise.all([
        svgUrlToPngDataUrl(cairnHorizontalLogo, 560, 150).catch(() => null),
        svgUrlToPngDataUrl(cairnMarkSlate, 128, 128).catch(() => null),
        svgUrlToPngDataUrl(dogCatOutline, 240, 150).catch(() => null),
        APP_STORE_URL ? svgUrlToPngDataUrl(appStoreBadge, 360, 120) : null,
        GOOGLE_PLAY_URL ? imageUrlToPngDataUrl(googlePlayBadge, 646, 250) : null,
      ]);

      const doc = buildPatientMonitoringGuideDoc({
        clinicName: clinic.name,
        clinicPhone: formatPhoneForDisplay(clinic.phone),
        enrollmentCode: clinic.enrollment_code,
        logoDataUrl,
        markDataUrl,
        petOutlineDataUrl,
        clinicLogoDataUrl: null, // Layout supports one; clinics do not yet have a logo field.
        enrollmentQrDataUrl,
        appStoreUrl: APP_STORE_URL || null,
        googlePlayUrl: GOOGLE_PLAY_URL || null,
        appStoreBadgeDataUrl,
        googlePlayBadgeDataUrl,
      });
      downloadPatientMonitoringGuide(doc, clinic.name);
    } catch (err) {
      setGuideError(err.message || t('clinicSettingsView.guideError'));
    } finally {
      setGenerating(false);
    }
  }

  function startEditingHospitals() {
    setHospitalError('');
    setHospitalForm({
      emergency_hospital_1_name:  clinic.emergency_hospital_1_name  || '',
      emergency_hospital_1_phone: clinic.emergency_hospital_1_phone || '',
      emergency_hospital_2_name:  clinic.emergency_hospital_2_name  || '',
      emergency_hospital_2_phone: clinic.emergency_hospital_2_phone || '',
    });
    setEditingHospitals(true);
  }

  async function saveHospitals() {
    setSavingHospitals(true);
    setHospitalError('');
    try {
      const payload = {
        emergency_hospital_1_name:  hospitalForm.emergency_hospital_1_name.trim()  || null,
        emergency_hospital_1_phone: hospitalForm.emergency_hospital_1_phone.trim() || null,
        emergency_hospital_2_name:  hospitalForm.emergency_hospital_2_name.trim()  || null,
        emergency_hospital_2_phone: hospitalForm.emergency_hospital_2_phone.trim() || null,
      };
      const { error } = await supabase.from('clinics').update(payload).eq('id', clinic.id);
      if (error) throw error;
      onClinicUpdate?.({ ...clinic, ...payload });
      setEditingHospitals(false);
    } catch (err) {
      setHospitalError(err.message || t('clinicSettingsView.hospitalSaveError'));
    } finally {
      setSavingHospitals(false);
    }
  }

  if (!clinic) return null;

  return (
    <div style={{ maxWidth: 640 }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: c.slateDeep, marginBottom: 24 }}>{t('clinicSettingsView.title')}</h2>

      {/* QR Code card */}
      <div style={{ backgroundColor: c.white, borderRadius: 12, border: `1px solid ${c.border}`, padding: 32, marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: c.slateDeep, marginBottom: 4 }}>{t('clinicSettingsView.ownerEnrollment')}</h3>
        <p style={{ fontSize: 13, color: c.textSoft, lineHeight: 1.6, marginBottom: 24 }}>
          {t('clinicSettingsView.enrollmentBody')}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <div style={{ padding: 20, backgroundColor: '#ffffff', borderRadius: 12, border: `1px solid ${c.border}`, display: 'inline-block' }}>
            <QRCodeCanvas
              id="clinic-qr-canvas"
              value={`https://cairnpethealth.com/enroll/?id=${clinic.id}`}
              size={600}
              style={{ width: 200, height: 200 }}
              bgColor="#ffffff"
              fgColor="#3a4d59"
              level="M"
              marginSize={4}
            />
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: c.slateDeep }}>{clinic.name}</div>
            <div style={{ fontSize: 11, color: c.textSoft, marginTop: 4, fontFamily: 'monospace' }}>{clinic.id}</div>
          </div>

          {clinic.enrollment_code && (
            <div style={{ textAlign: 'center', padding: '16px 24px', backgroundColor: c.fog, borderRadius: 12, border: `1px solid ${c.border}`, width: '100%' }}>
              <div style={{ fontSize: 11, color: c.textSoft, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>{t('enrollment.code')}</div>
              <div style={{ fontSize: 32, fontWeight: 700, color: c.slateDeep, fontFamily: 'monospace', letterSpacing: 8 }}>
                {clinic.enrollment_code}
              </div>
              <div style={{ fontSize: 12, color: c.textSoft, marginTop: 6 }}>
                {t('clinicSettingsView.shareCodeHint')}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={downloadQR}
            style={{ padding: '10px 20px', backgroundColor: c.moss, color: c.white, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            {t('enrollment.downloadQr')}
          </button>
          <button
            onClick={copyCode}
            style={{ padding: '10px 20px', backgroundColor: copiedCode ? c.greenLight : 'transparent', color: copiedCode ? c.moss : c.slate, border: `1px solid ${copiedCode ? c.moss : c.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}
          >
            {copiedCode ? t('clinicSettingsView.codeCopiedBtn') : t('enrollment.copyCode')}
          </button>
          <button
            onClick={copyEnrollmentLink}
            style={{ padding: '10px 20px', backgroundColor: copied ? c.greenLight : 'transparent', color: copied ? c.moss : c.slate, border: `1px solid ${copied ? c.moss : c.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}
          >
            {copied ? t('clinicSettingsView.linkCopiedBtn') : t('clinicSettingsView.copyLink')}
          </button>
          <button
            onClick={handleDownloadGuide}
            disabled={generating}
            aria-busy={generating}
            style={{ padding: '10px 20px', backgroundColor: 'transparent', color: c.slate, border: `1px solid ${c.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: generating ? 'default' : 'pointer', opacity: generating ? 0.7 : 1 }}
          >
            {generating ? t('clinicSettingsView.generating') : t('clinicSettingsView.downloadGuide')}
          </button>
        </div>

        {guideError && (
          <p role="alert" style={{ fontSize: 12, color: c.error, marginTop: 12, textAlign: 'center' }}>{guideError}</p>
        )}

      </div>

      {/* Clinic info */}
      <div style={{ backgroundColor: c.white, borderRadius: 12, border: `1px solid ${c.border}`, padding: 24, marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: c.slateDeep, marginBottom: 16 }}>{t('clinicSettingsView.clinicInfo')}</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ color: c.textSoft }}>{t('clinicSettingsView.clinicNameLabel')}</span>
            <span style={{ color: c.slate, fontWeight: 500 }}>{clinic.name}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ color: c.textSoft }}>{t('clinicSettingsView.clinicIdLabel')}</span>
            <span style={{ color: c.slate, fontFamily: 'monospace', fontSize: 11 }}>{clinic.id}</span>
          </div>
        </div>
      </div>

      {/* Emergency hospitals — feeds the guide's "Emergency veterinary care"
          section. Editable only by clinic managers/admins, matching the
          existing manager-gated RLS policy on this table (migration 75/76). */}
      <div style={{ backgroundColor: c.white, borderRadius: 12, border: `1px solid ${c.border}`, padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: c.slateDeep }}>{t('clinicSettingsView.emergencyHospitals')}</h3>
          {canManage && !editingHospitals && (
            <button
              onClick={startEditingHospitals}
              style={{ padding: '6px 14px', backgroundColor: 'transparent', color: c.slate, border: `1px solid ${c.border}`, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            >
              {t('common.edit')}
            </button>
          )}
        </div>
        <p style={{ fontSize: 13, color: c.textSoft, lineHeight: 1.6, marginBottom: 16 }}>
          {t('clinicSettingsView.hospitalsBody')}
        </p>

        {!editingHospitals ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[1, 2].map(n => {
              const name  = clinic[`emergency_hospital_${n}_name`];
              const phone = clinic[`emergency_hospital_${n}_phone`];
              if (!name) return (
                <div key={n} style={{ fontSize: 13, color: c.textSoft, fontStyle: 'italic' }}>{t('clinicSettingsView.hospitalNotConfigured', { n })}</div>
              );
              return (
                <div key={n} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: c.slate, fontWeight: 500 }}>{name}</span>
                  <span style={{ color: c.textSoft }}>{formatPhoneForDisplay(phone) || '—'}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[1, 2].map(n => (
              <div key={n} style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder={t('clinicSettingsView.hospitalNamePlaceholder', { n })}
                  value={hospitalForm[`emergency_hospital_${n}_name`]}
                  onChange={e => setHospitalForm(f => ({ ...f, [`emergency_hospital_${n}_name`]: e.target.value }))}
                  aria-label={t('clinicSettingsView.hospitalNameA11y', { n })}
                  style={{ flex: '1 1 240px', fontSize: 13, padding: '8px 12px', borderRadius: 6, border: `1px solid ${c.border}`, outline: 'none', color: c.slate }}
                />
                <input
                  type="text"
                  placeholder={t('clinicSettingsView.hospitalPhonePlaceholder')}
                  value={hospitalForm[`emergency_hospital_${n}_phone`]}
                  onChange={e => setHospitalForm(f => ({ ...f, [`emergency_hospital_${n}_phone`]: e.target.value }))}
                  aria-label={t('clinicSettingsView.hospitalPhoneA11y', { n })}
                  style={{ flex: '1 1 160px', fontSize: 13, padding: '8px 12px', borderRadius: 6, border: `1px solid ${c.border}`, outline: 'none', color: c.slate }}
                />
              </div>
            ))}
            {hospitalError && <p role="alert" style={{ fontSize: 12, color: c.error }}>{hospitalError}</p>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={saveHospitals}
                disabled={savingHospitals}
                style={{ padding: '8px 18px', backgroundColor: c.moss, color: c.white, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: savingHospitals ? 'default' : 'pointer', opacity: savingHospitals ? 0.7 : 1 }}
              >
                {savingHospitals ? t('permissions.saving') : t('common.save')}
              </button>
              <button
                onClick={() => setEditingHospitals(false)}
                disabled={savingHospitals}
                style={{ padding: '8px 18px', backgroundColor: 'transparent', color: c.slate, border: `1px solid ${c.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
